/*
 * Naturlabs PDP v3 (sections nl-v3-pdp-*). Vanilla, no dependencies, loaded with defer.
 *
 * - [data-v3-submit]: any button outside the main buy box (anchor bar, owner card, bottom bar) submits the main
 *   product form of the page (selected variant and quantity), so the cart drawer and errors behave like the main CTA.
 * - <v3-buybar>: bottom buy bar below 1024 px. Visible whenever the main add to cart button is off screen (above or
 *   below), hidden over the footer and while the cookie consent banner is open.
 * - <v3-anchors>: removes links whose section is missing, numbers the remaining links and the section eyebrows in page
 *   order, marks the active link, shows the mini product with price and CTA once the main button scrolled away.
 * - <v3-video>: muted loop that plays only while on screen; never autoplays under prefers-reduced-motion or with the
 *   motion setting off (html.nl-motion-off); play / pause toggle for everybody.
 * - [data-v3-fixed]: the product of the page stays selected in the optional set.
 * Motion: transform and opacity only (CSS), nothing here animates layout.
 */
(() => {
  if (window.NLv3Pdp) return;
  window.NLv3Pdp = true;

  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };
  const reduced = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    document.documentElement.classList.contains('nl-motion-off');
  /* Data Saver: video stays on its poster until the visitor presses play */
  const saveData = () => Boolean(navigator.connection && navigator.connection.saveData);

  const mainForm = () => document.querySelector('[data-v3-main] form[id^="ProductForm-"]');
  const mainAtc = () => document.querySelector('[data-v3-main] [data-v3-main-atc]');

  /* ---------- Submit the main form from secondary CTAs ---------- */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-v3-submit]');
    if (!button || button.disabled) return;
    const form = mainForm();
    if (!form) return;
    event.preventDefault();
    const atc = mainAtc();
    if (atc && atc.disabled) return;
    if (typeof form.requestSubmit === 'function') form.requestSubmit(atc || undefined);
    else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  });

  /* ---------- Optional set: this product stays selected ---------- */
  document.addEventListener(
    'change',
    (event) => {
      const box = event.target.closest('[data-v3-fixed]');
      if (box && !box.checked) {
        box.checked = true;
      }
    },
    true
  );

  /* ---------- Cookie consent banner (keeps the bottom bar out of its way) ---------- */
  const CONSENT = '#shopify-pc__banner, .shopify-pc__banner__dialog';
  const consentOpen = () => {
    const banner = document.querySelector(CONSENT);
    if (!banner) return false;
    const style = getComputedStyle(banner);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
    const rect = banner.getBoundingClientRect();
    return rect.height > 0 && rect.width > 0 && rect.top < window.innerHeight;
  };
  const consentListeners = new Set();
  let consentState = null;
  const syncConsent = () => {
    const open = consentOpen();
    if (open === consentState) return;
    consentState = open;
    document.documentElement.toggleAttribute('data-v3-consent-open', open);
    consentListeners.forEach((fn) => fn(open));
  };
  let consentWatching = false;
  const watchConsent = () => {
    if (consentWatching) return;
    consentWatching = true;
    syncConsent();
    let checks = 0;
    const timer = window.setInterval(() => {
      syncConsent();
      checks += 1;
      if (checks >= 20) window.clearInterval(timer);
    }, 750);
    if ('MutationObserver' in window) {
      let frame = 0;
      new MutationObserver(() => {
        if (frame) return;
        frame = window.requestAnimationFrame(() => {
          frame = 0;
          syncConsent();
        });
      }).observe(document.body, { childList: true, subtree: false, attributes: true, attributeFilter: ['class', 'style'] });
    }
    document.addEventListener('click', () => window.setTimeout(syncConsent, 300), true);
  };

  /* ---------- <v3-buybar> ---------- */
  class V3Buybar extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this.target = this.dataset.target ? document.querySelector(this.dataset.target) : null;
      this.panel = this.firstElementChild;
      if (!this.target || !this.panel || !('IntersectionObserver' in window)) return;
      this._bound = true;
      this.offscreen = false;
      this.overFooter = false;
      this.consent = false;
      this.panel.inert = true;

      this.io = new IntersectionObserver(([entry]) => {
        this.offscreen = !entry.isIntersecting;
        this.update();
      });
      this.io.observe(this.target);

      const footer = document.querySelector('.nl-footer, footer');
      if (footer) {
        this.fo = new IntersectionObserver(([entry]) => {
          this.overFooter = entry.isIntersecting;
          this.update();
        });
        this.fo.observe(footer);
      }
      this.onConsent = (open) => {
        this.consent = open;
        this.update();
      };
      consentListeners.add(this.onConsent);
      watchConsent();
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.fo) this.fo.disconnect();
      consentListeners.delete(this.onConsent);
      this._bound = false;
    }

    update() {
      const visible = this.offscreen && !this.overFooter && !this.consent;
      this.toggleAttribute('data-visible', visible);
      this.panel.inert = !visible;
      document.documentElement.toggleAttribute('data-v3-buybar', visible);
    }
  }
  define('v3-buybar', V3Buybar);

  /* ---------- Section numbering shared by the anchor bar and the section eyebrows ---------- */
  const ORDER = ['v3-na-co', 'v3-slozeni', 'v3-jak-uzivat', 'v3-pro-koho', 'v3-otazky'];
  const pad = (n) => (n < 10 ? `0${n}` : String(n));
  const numberSections = () => {
    const present = ORDER.map((id) => document.getElementById(id)).filter(Boolean);
    present.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
    const map = new Map();
    present.forEach((section, i) => {
      map.set(section.id, pad(i + 1));
      const idx = section.querySelector('.nl-v3-head .nl-v3-eyebrow__idx');
      if (idx) idx.textContent = pad(i + 1);
    });
    return map;
  };

  /* ---------- <v3-anchors> ---------- */
  class V3Anchors extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      const numbers = numberSections();
      this.links = Array.from(this.querySelectorAll('[data-v3-anchor-link]')).filter((link) => {
        const id = (link.getAttribute('href') || '').slice(1);
        if (!numbers.has(id)) {
          const li = link.closest('li');
          (li || link).remove();
          return false;
        }
        const n = link.querySelector('[data-v3-anchor-n]');
        if (n) n.textContent = numbers.get(id);
        return true;
      });
      if (!this.links.length) {
        this.hidden = true;
        return;
      }
      this.links.sort((a, b) => Number(numbers.get(a.getAttribute('href').slice(1))) - Number(numbers.get(b.getAttribute('href').slice(1))));
      const list = this.querySelector('.nl-v3-anchors__list');
      if (list) {
        this.links.forEach((link) => {
          const li = link.closest('li');
          if (li && li.parentElement === list) list.appendChild(li);
        });
      }
      this.watchActive();
      this.watchBuy();
      /* Final 17. 10. (M9): přechod vpravo na posuvné řadě kotev zmizí na konci řady (nebo když se řada vejde celá) */
      const nav = this.querySelector('.nl-v3-anchors__nav');
      if (nav) {
        const syncEnd = () => {
          const max = nav.scrollWidth - nav.clientWidth;
          nav.classList.toggle('is-fit', max <= 2);
          nav.classList.toggle('is-end', max > 2 && nav.scrollLeft >= max - 2);
        };
        nav.addEventListener('scroll', syncEnd, { passive: true });
        window.addEventListener('resize', syncEnd, { passive: true });
        syncEnd();
      }
      this.onVariant = (event) => {
        const { variant } = event.detail || {};
        const price = this.querySelector('[data-v3-price]');
        if (!variant || !price) return;
        try {
          if (window.NL && window.NL.formatMoney) price.textContent = window.NL.formatMoney(variant.price, { trimZeros: true });
        } catch (error) {
          /* keep the server price */
        }
      };
      document.addEventListener('variant:change', this.onVariant);
    }

    disconnectedCallback() {
      if (this.activeIo) this.activeIo.disconnect();
      if (this.buyIo) this.buyIo.disconnect();
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('variant:change', this.onVariant);
      this._bound = false;
    }

    watchActive() {
      if (!('IntersectionObserver' in window)) return;
      const targets = this.links.map((link) => document.getElementById(link.getAttribute('href').slice(1)));
      const visible = new Map();
      this.activeIo = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => visible.set(entry.target.id, entry.isIntersecting));
          let current = null;
          for (const target of targets) {
            if (visible.get(target.id)) {
              current = target.id;
              break;
            }
          }
          this.links.forEach((link) => {
            const on = current && link.getAttribute('href') === `#${current}`;
            if (on) link.setAttribute('aria-current', 'true');
            else link.removeAttribute('aria-current');
            if (on) {
              const nav = link.closest('.nl-v3-anchors__nav');
              if (nav && nav.scrollWidth > nav.clientWidth) {
                const left = link.offsetLeft - (nav.clientWidth - link.offsetWidth) / 2;
                nav.scrollTo({ left, behavior: reduced() ? 'auto' : 'smooth' });
              }
            }
          });
        },
        { rootMargin: '-35% 0px -55% 0px' }
      );
      targets.forEach((target) => this.activeIo.observe(target));
    }

    watchBuy() {
      /* Vyprodaný nebo nenabízený produkt nemá tlačítko do košíku: lišta kotev se řídí neutrálním stavem v nákupním sloupci */
      const atc = mainAtc() || document.querySelector('[data-v3-main] [data-v3-buy-alt]');
      if (!atc || !('IntersectionObserver' in window)) {
        this.toggleAttribute('data-buy', true);
        return;
      }
      const cta = this.querySelector('[data-v3-anchors-cta]');
      let state = null;
      const apply = (past) => {
        if (past === state) return;
        state = past;
        this.toggleAttribute('data-buy', past);
        if (cta) cta.inert = !past;
      };
      this.buyIo = new IntersectionObserver(([entry]) => {
        apply(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
      this.buyIo.observe(atc);
      /* A jump (Home key, anchor link, restored scroll) can move the button from above the viewport to below it
         without any intersection change, so the state is also read once per frame after scrolling. */
      let frame = 0;
      this.onScroll = () => {
        if (frame) return;
        frame = window.requestAnimationFrame(() => {
          frame = 0;
          apply(atc.getBoundingClientRect().bottom < 0);
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      apply(false);
    }
  }
  define('v3-anchors', V3Anchors);

  /* ---------- <v3-video> ---------- */
  class V3Video extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this.video = this.querySelector('video');
      this.toggle = this.querySelector('[data-v3-video-toggle]');
      if (!this.video) return;
      this._bound = true;
      this.userPaused = false;
      this.inView = false;
      this.video.muted = true;
      /* Inside a gallery slide the video plays only while that slide is the active one (and on screen). */
      this.slide = this.closest('[data-gallery-slide]');
      this.gallery = this.slide ? this.closest('media-gallery') : null;
      this.video.addEventListener('play', () => this.sync());
      this.video.addEventListener('pause', () => this.sync());
      this.video.addEventListener('playing', () => this.setAttribute('data-started', ''));
      if (this.gallery) {
        this.onGallery = () => this.evaluate();
        this.gallery.addEventListener('gallery:change', this.onGallery);
      }
      if (this.toggle) {
        this.toggle.addEventListener('click', () => {
          if (this.video.paused) {
            this.userPaused = false;
            this.play();
          } else {
            this.userPaused = true;
            this.video.pause();
          }
        });
      }
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          ([entry]) => {
            this.inView = entry.isIntersecting;
            this.evaluate();
          },
          { threshold: 0.35 }
        );
        this.io.observe(this);
      }
      this.sync();
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.gallery && this.onGallery) this.gallery.removeEventListener('gallery:change', this.onGallery);
      this._bound = false;
    }

    evaluate() {
      const active = !this.slide || this.slide.hasAttribute('data-active');
      if (!this.inView || !active) {
        if (!this.video.paused) this.video.pause();
        return;
      }
      if (!this.userPaused && !reduced() && !saveData()) this.play();
    }

    play() {
      // No preload="auto" here: play() buffers progressively, a video scrolled past does not pull the whole file
      const p = this.video.play();
      if (p && typeof p.catch === 'function') p.catch(() => this.sync());
    }

    sync() {
      const playing = !this.video.paused;
      this.toggleAttribute('data-playing', playing);
      if (!this.toggle) return;
      this.toggle.setAttribute('aria-pressed', String(playing));
      this.toggle.setAttribute('aria-label', playing ? this.toggle.dataset.labelPause : this.toggle.dataset.labelPlay);
    }
  }
  define('v3-video', V3Video);

  /* polish-v3 B4: [data-nl-pair-add] adds this product and the first rail product in one /cart/add.js call at
     regular prices (no discount); the cart drawer opens like after the main CTA. */
  document.addEventListener('click', async (event) => {
    const btn = event.target.closest('[data-nl-pair-add]');
    if (!btn || btn.disabled) return;
    let items = [];
    try {
      items = JSON.parse(btn.dataset.nlPairAdd || '[]');
    } catch (e) {
      items = [];
    }
    if (!items.length) return;
    const box = btn.parentElement;
    const err = box && box.querySelector('[data-nl-pair-error]');
    if (err) err.hidden = true;
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    try {
      if (window.NL && window.NL.cart && typeof window.NL.cart.add === 'function') {
        await window.NL.cart.add(items, { source: 'pdp-pair', opener: btn });
      } else {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!res.ok) throw new Error('add');
        window.location.href = '/cart';
      }
    } catch (e) {
      if (err) {
        err.textContent = (e && e.message && e.message !== 'add' && e.message) || (window.NL && window.NL.strings && window.NL.strings.cartError) || '';
        err.hidden = false;
      }
    } finally {
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
    }
  });

  /* Final 17. 10. 2026 (B7, JM-08): Vybrat do dvojice v Doplňte rituál přepne pár. Řádek nese data-pair-items a hotový
     popisek ze serveru (products.final_addon_pair, jmenuje oba produkty a součet), tlačítko páru je jen převezme;
     štítek Ve dvojici se přesune na vybraný řádek a fokus na tlačítko páru, které nový pár přečte. */
  document.addEventListener('click', (event) => {
    const pick = event.target.closest && event.target.closest('[data-nl-pair-pick]');
    if (!pick) return;
    const row = pick.closest('[data-nl-pair-row]');
    const box = pick.closest('[data-pdp-addon]');
    const btn = box && box.querySelector('[data-nl-pair-add]');
    if (!row || !btn || !row.dataset.pairItems) return;
    event.preventDefault();
    box.querySelectorAll('[data-nl-pair-row]').forEach((r) => {
      const on = r === row;
      r.classList.toggle('is-paired', on);
      const badge = r.querySelector('[data-nl-pair-badge]');
      const choose = r.querySelector('[data-nl-pair-pick]');
      if (badge) badge.hidden = !on;
      if (choose) choose.hidden = on;
    });
    btn.dataset.nlPairAdd = row.dataset.pairItems;
    const label = btn.querySelector('[data-nl-pair-label]');
    if (label && row.dataset.pairLabel) label.textContent = row.dataset.pairLabel;
    btn.focus({ preventScroll: true });
  });

  /* Final 17. 10. 2026 (JM-09, M11): u vyprodaného a nenabízeného produktu tlačítko Hlídat dostupnost v liště i v liště
     kotev posune na formulář a dá fokus do e-mailu (bez druhého skoku stránky). */
  document.addEventListener('click', (event) => {
    const link = event.target.closest && event.target.closest('a[href^="#Notify-"], a[data-nl-notify-jump]');
    if (!link) return;
    const id = (link.getAttribute('href') || '').slice(1);
    const region = (id && document.getElementById(id)) || document.querySelector('[data-v3-main] [id^="Notify-"]');
    if (!region) return;
    const input = region.querySelector('input[type="email"]');
    event.preventDefault();
    region.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'center' });
    if (input) input.focus({ preventScroll: true });
  });

  /* Owner 16. 10. 2026: šipky Předchozí a Další obrázek na hlavním snímku galerie (snippets/nl-v5-pdp-gallery.liquid).
     Volají goTo komponenty media-gallery (assets/nl-app.js), takže se posune i aktivní náhled v pruhu, tečky,
     počítadlo a videa se pauzují stejně jako po klepnutí na náhled. Na prvním a posledním snímku jsou zakázané.
     Šipky vlevo a vpravo fungují i s fokusem na šipkách a náhledech (pruh snímků je obsluhuje sám). */
  const syncArrows = (gallery) => {
    const prev = gallery.querySelector('[data-gallery-prev]');
    const next = gallery.querySelector('[data-gallery-next]');
    if (!prev || !next) return;
    const count = gallery.querySelectorAll('[data-gallery-slide]').length;
    const index = typeof gallery.index === 'number' ? gallery.index : 0;
    prev.disabled = index <= 0;
    next.disabled = index >= count - 1;
  };
  const stepGallery = (gallery, delta) => {
    if (!gallery || typeof gallery.goTo !== 'function') return;
    const index = typeof gallery.index === 'number' ? gallery.index : 0;
    gallery.goTo(index + delta);
    syncArrows(gallery);
  };
  document.addEventListener('click', (event) => {
    const btn = event.target.closest && event.target.closest('[data-gallery-prev], [data-gallery-next]');
    if (!btn || btn.disabled) return;
    event.preventDefault();
    stepGallery(btn.closest('media-gallery'), btn.hasAttribute('data-gallery-prev') ? -1 : 1);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    const el = event.target;
    if (!el || !el.closest || !el.closest('[data-gallery-prev], [data-gallery-next], [data-gallery-thumb]')) return;
    const gallery = el.closest('media-gallery');
    if (!gallery || !gallery.querySelector('[data-gallery-prev]')) return;
    event.preventDefault();
    stepGallery(gallery, event.key === 'ArrowLeft' ? -1 : 1);
  });
  document.addEventListener('gallery:change', (event) => {
    const gallery = event.target && event.target.closest ? event.target.closest('media-gallery') : null;
    if (gallery) syncArrows(gallery);
  });
  const bindArrows = () => {
    const galleries = [...document.querySelectorAll('media-gallery')].filter((g) => g.querySelector('[data-gallery-prev]'));
    if (!galleries.length) return;
    const run = () => galleries.forEach(syncArrows);
    if (window.customElements && customElements.whenDefined) customElements.whenDefined('media-gallery').then(run);
    else run();
  };

  /* owner 16. 10. 2026 18:30: Doplňte rituál v nákupním sloupci (snippets/nl-pdp-addon.liquid). Tlačítko + se při
     ťuknutí hned přepne na fajfku (šuplík kreslí řádek také hned, assets/nl-w2-cart.js); po odpovědi ho vrátí
     product-form (flashAdded), při chybě hned tady. */
  document.addEventListener('cart:adding', (event) => {
    const opener = event.detail && event.detail.opener;
    const btn = opener && opener.closest ? opener.closest('.nl-pdp-addon__add') : null;
    if (!btn) return;
    btn.dataset.addToken = String(event.detail.token);
    btn.classList.add('is-added');
  });
  document.addEventListener('cart:error', (event) => {
    const token = event.detail && event.detail.token != null ? String(event.detail.token) : '';
    document.querySelectorAll('.nl-pdp-addon__add.is-added').forEach((btn) => {
      if (!token || btn.dataset.addToken === token) btn.classList.remove('is-added');
    });
  });

  /* Jar sheen mask ([data-jar-mask], gallery and closing band): the mask takes the URL the jar image actually loaded
     (same file from the cache) instead of a fixed width 720 that was a second download of the LCP image */
  const bindJarMask = (box) => {
    const img = box.querySelector('img');
    if (!img || box.dataset.jarMaskBound) return;
    box.dataset.jarMaskBound = '1';
    const apply = () => {
      const src = img.currentSrc || img.src;
      if (src) box.style.setProperty('--jar-mask', `url("${src.replace(/"/g, '%22')}")`);
    };
    if (img.complete && img.naturalWidth) apply();
    else img.addEventListener('load', apply, { once: true });
  };
  const bindJarMasks = () => document.querySelectorAll('[data-jar-mask]').forEach(bindJarMask);

  /* owner 16. 10. 2026 18:30: S2 Tvrzení, karta vpravo jede se scrollem do konce obrázku (assets/nl-pdp2.css,
     is-cards-stick). Lepí se jen od 1024 px, jen když je karta nižší než obrázek a celá se vejde do okna pod lištu
     kotev; jinak zůstává v toku a obrázek se lepí jako dřív. */
  const claimsWide = window.matchMedia('(min-width: 1024px)');
  let claimsFrame = 0;
  const fitClaims = () => {
    claimsFrame = 0;
    document.querySelectorAll('.nl-pdp2-claims.has-img').forEach((sec) => {
      const cards = sec.querySelector('.nl-pdp2-claims__cards');
      const media = sec.querySelector('.nl-pdp2-claims__media');
      if (!cards || !media) return;
      let stick = false;
      if (claimsWide.matches) {
        /* top počítá s viditelnou hlavičkou (vrací se při scrollu nahoru), ať se karta nikdy neschová pod okraj */
        const shown = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--nl-header-offset')) || 0;
        const header = document.querySelector('.nl-header-section');
        const headerH = header ? header.getBoundingClientRect().height : 0;
        const top = (parseFloat(getComputedStyle(cards).top) || 96) - shown + Math.max(shown, headerH);
        const h = cards.offsetHeight;
        stick = h + 40 < media.offsetHeight && h + top + 16 <= window.innerHeight;
      }
      sec.classList.toggle('is-cards-stick', stick);
    });
  };
  const queueClaims = () => {
    if (!claimsFrame) claimsFrame = window.requestAnimationFrame(fitClaims);
  };
  const bindClaims = () => {
    const secs = document.querySelectorAll('.nl-pdp2-claims.has-img');
    if (!secs.length) return;
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(queueClaims);
      secs.forEach((sec) => sec.querySelectorAll('.nl-pdp2-claims__cards, .nl-pdp2-claims__media').forEach((el) => ro.observe(el)));
    }
    window.addEventListener('resize', queueClaims, { passive: true });
    queueClaims();
  };

  /* Section numbering also without the anchor bar */
  const ready = () => {
    if (!document.querySelector('v3-anchors')) numberSections();
    bindJarMasks();
    bindClaims();
    bindArrows();
  };
  document.addEventListener('shopify:section:load', bindJarMasks);
  document.addEventListener('shopify:section:load', bindClaims);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready);
  else ready();
})();
